import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { SimpleTooltip } from '@eluve/components';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Icon,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
} from '@eluve/components';

const importanceOptions = [
  { label: 'Critical (0.8-1.0)', value: '1.0' },
  { label: 'Important (0.6-0.8)', value: '0.8' },
  { label: 'Useful (0.4-0.6)', value: '0.6' },
  { label: 'Relevant (0.2-0.4)', value: '0.4' },
  { label: 'Minor (0.0-0.2)', value: '0.2' },
];

const factFormSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  statement: z.string().nullable(),
  expectedOutput: z.boolean(),
  weightMultiplier: z.number().min(0).max(1),
  isVerified: z.boolean(),
});

export type GoldenSummaryFactFormData = z.infer<typeof factFormSchema>;

interface GoldenSummaryFactFormProps {
  data: GoldenSummaryFactFormData;
  onSaveRequested: (data: GoldenSummaryFactFormData) => void;
  onDeleteRequested: (id: string) => void;
  isSubmitting?: boolean;
}

export const GoldenSummaryFactForm: FC<GoldenSummaryFactFormProps> = ({
  data,
  isSubmitting = false,
  onSaveRequested,
  onDeleteRequested,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const initialValuesRef = useRef(JSON.stringify(data));

  const formData = factFormSchema.safeParse(data).success
    ? factFormSchema.safeParse(data).data
    : data;

  const form = useForm<GoldenSummaryFactFormData>({
    resolver: zodResolver(factFormSchema),
    defaultValues: formData,
  });

  const handleSaveClick = () => {
    setIsEditing(false);
    const formData = form.getValues();
    onSaveRequested(formData);
    initialValuesRef.current = JSON.stringify(formData);
  };

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const currentValues = JSON.stringify(formValues);
      if (currentValues !== initialValuesRef.current) {
        setIsEditing(true);
      } else {
        setIsEditing(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [form]);

  useEffect(() => {
    if (!isEditing) {
      form.reset(data);
      initialValuesRef.current = JSON.stringify(data);
    }
  }, [data, form, isEditing]);

  const handleDeleteClick = () => {
    onDeleteRequested(data.id);
  };

  return (
    <Form {...form}>
      <FormField
        control={form.control}
        name="statement"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-semibold">Fact Statement</FormLabel>
            <FormControl>
              <Input
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e);
                }}
                placeholder="Enter the fact statement e.g. CC is persistent cough"
                disabled={isSubmitting}
              />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="weightMultiplier"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="font-semibold">Importance Level</FormLabel>
            <FormControl>
              <Select
                value={field.value?.toFixed(1)}
                onValueChange={(value) => {
                  const newValue = parseFloat(value);
                  field.onChange(newValue);
                }}
                disabled={isSubmitting}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select importance level" />
                </SelectTrigger>
                <SelectContent>
                  {importanceOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
          </FormItem>
        )}
      />
      <Stack gap={6} direction="horizontal">
        <FormField
          control={form.control}
          name="expectedOutput"
          render={({ field }) => (
            <FormItem className="mb-3 flex flex-col justify-between">
              <FormLabel className="font-semibold">Type of Fact</FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value ? 'true' : 'false'}
                  onValueChange={(value) => {
                    const newValue = value === 'true';
                    field.onChange(newValue);
                  }}
                  className="flex gap-4"
                  disabled={isSubmitting}
                >
                  <Stack gap={2} align="start">
                    <SimpleTooltip label="This fact must be true/present in the transcript">
                      <Label className="flex cursor-pointer gap-3">
                        <RadioGroupItem
                          id="true-fact"
                          value="true"
                          disabled={isSubmitting}
                        />
                        True
                      </Label>
                    </SimpleTooltip>
                    <SimpleTooltip label="This fact must be false/absent in the transcript">
                      <Label className="flex cursor-pointer gap-3">
                        <RadioGroupItem
                          id="false-fact"
                          value="false"
                          disabled={isSubmitting}
                        />
                        False
                      </Label>
                    </SimpleTooltip>
                  </Stack>
                </RadioGroup>
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="isVerified"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center gap-2 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormLabel className="font-medium">Verified Fact</FormLabel>
            </FormItem>
          )}
        />
      </Stack>
      <Stack gap={3} direction="horizontal" align="center" justify="end">
        <SimpleTooltip label="Save fact">
          <Button
            onClick={handleSaveClick}
            disabled={isSubmitting || !isEditing}
          >
            <Icon name="Save" size="auto" />
          </Button>
        </SimpleTooltip>
        <AlertDialog>
          <SimpleTooltip label="Delete fact">
            <AlertDialogTrigger asChild>
              <Button variant="secondary">
                <Icon name="Trash" size="auto" />
              </Button>
            </AlertDialogTrigger>
          </SimpleTooltip>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Fact</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this fact? This action cannot be
                undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSubmitting}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-bg-negative-strong-primary hover:bg-bg-negative-strong-primary/90"
                onClick={handleDeleteClick}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Stack>
    </Form>
  );
};
